/**
 * 수출입 자동분류 거래처 명단 API — backend /purchases/import-partners/* 와 1:1 (v2.131.0).
 */

const BASE = '/api';

export type ImportRouteKind = '물류' | '부가세';

export interface PartnerStat {
  count: number;
  amount: number;
  lastDate: string | null;
}

export interface ImportPartnerRow {
  id: number;
  route: ImportRouteKind;
  bno: string;
  nameToken: string;
  displayName: string;
  note: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  stats: PartnerStat;
}

export interface ImportPartnerList {
  rows: ImportPartnerRow[];
  builtin: { label: string; route: ImportRouteKind; stats: PartnerStat };
  orphan: PartnerStat;
  /** 표를 못 읽어 씨앗 명단으로 도는 중 — 화면에 경고를 띄운다 */
  fallback: boolean;
}

export interface PartnerCandidate {
  bno: string;
  supplier: string;
  count: number;
  amount: number;
  lastDate: string | null;
}

export interface PartnerInput {
  route: ImportRouteKind;
  displayName: string;
  bno: string;
  matchByName: boolean;
  nameToken: string;
  note: string;
  isActive: boolean;
}

async function handle<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => null);
  if (!res.ok || (json && json.ok === false) || (json && json.error)) {
    const msg = json?.error?.message ?? json?.message ?? `요청 실패 (${res.status})`;
    throw new Error(msg);
  }
  return json as T;
}

export async function listPartners(): Promise<ImportPartnerList> {
  const res = await fetch(`${BASE}/purchases/import-partners`, { credentials: 'include' });
  return handle<ImportPartnerList>(res);
}

export async function listCandidates(): Promise<PartnerCandidate[]> {
  const res = await fetch(`${BASE}/purchases/import-partners/candidates`, { credentials: 'include' });
  const json = await handle<{ rows: PartnerCandidate[] }>(res);
  return json.rows;
}

/**
 * 추가 — DHL 은 서버가 확인을 요구한다(CONFLICT · reason=DHL_CONFIRM_REQUIRED).
 * onConfirmDhl 이 true 를 주면 confirmDhl 로 다시 보낸다.
 * (매입 삭제 경고·수입오더 중복 경고와 같은 관례)
 */
export async function createPartner(
  input: PartnerInput,
  onConfirmDhl?: (message: string) => boolean | Promise<boolean>,
): Promise<{ id: number }> {
  const send = (confirmDhl: boolean): Promise<Response> =>
    fetch(`${BASE}/purchases/import-partners`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...input, confirmDhl }),
    });

  const res = await send(false);
  if (res.status === 409) {
    const json = await res.clone().json().catch(() => null) as
      { error?: { message?: string; details?: { reason?: string } } } | null;
    if (json?.error?.details?.reason === 'DHL_CONFIRM_REQUIRED' && onConfirmDhl) {
      const goAhead = await onConfirmDhl(json.error.message ?? 'DHL 을 명단에 넣으시겠습니까?');
      if (!goAhead) throw new Error('취소했습니다.');
      return handle<{ id: number }>(await send(true));
    }
  }
  return handle<{ id: number }>(res);
}

export async function updatePartner(id: number, input: PartnerInput): Promise<void> {
  const res = await fetch(`${BASE}/purchases/import-partners/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ...input, confirmDhl: true }),
  });
  await handle<{ ok: boolean }>(res);
}

export async function deletePartner(id: number): Promise<void> {
  const res = await fetch(`${BASE}/purchases/import-partners/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await handle<{ ok: boolean }>(res);
}
